import * as React from "react";
import { Account, Person } from "@/types";

import { cleanedErrors } from "@/utils/Functions";
import { RadioGroupInput } from "@/components/atoms";
import { FormField } from "@/components/molecules/FormField";
import { FormStack } from "@/components/molecules/FormStack";
import { useUpdater } from "@/utils/callbacks";
import {
  PersonDetailsFields,
  errorsOf as errorsOfPersonDetails,
} from "../ResponsibleFields/PersonDetailsFields";
import { defaultResponsible, fieldsFromPersons } from "../ResponsibleFields/responsible";
import { NestedErrors } from "@/utils/Types";

export type ExistingOccupier = {
  readonly responsibleId?: string;
  readonly responsible?: Person;
};

export type ExistingOccupierErrors = NestedErrors<ExistingOccupier> | undefined;

export const errorsOfExistingOccupier: (r?: ExistingOccupier) => ExistingOccupierErrors = (
  record
) => {
  return cleanedErrors({
    responsibleId: record?.responsibleId || record?.responsibleId === "" ? undefined : "required",
    responsible:
      record?.responsibleId !== "" ? undefined : errorsOfPersonDetails(record?.responsible, true),
  });
};

export const recordFromAccount = (account: Account | undefined): ExistingOccupier => {
  if (!account) return {};
  return { responsibleId: account.responsibleId };
};

export type ExistingOccupierFieldsProps = {
  readonly serviceProvider: { customForm?: string };
  readonly persons: ReadonlyArray<Person>;
  readonly labelOverride?: string;
  readonly record: ExistingOccupier;
  readonly errors: ExistingOccupierErrors;
  readonly update: any;
};

export const ExistingOccupierFields: React.FC<ExistingOccupierFieldsProps> = ({
  serviceProvider,
  persons,
  labelOverride = "Who will take over the account?",
  record,
  errors,
  update,
}) => {
  const onExistingOccupierIdChange = React.useCallback(
    (responsibleId) => {
      if (responsibleId) {
        update({ responsibleId });
      } else if (responsibleId === "") {
        update({ responsibleId, responsible: {} });
      } else {
        update({});
      }
    },
    [update]
  );

  const updateExistingOccupier = useUpdater(update, "responsible");

  React.useEffect(() => {
    if (typeof record.responsibleId === "string") return;

    const person = defaultResponsible(persons);

    if (person) onExistingOccupierIdChange(person.id);
  }, [record, persons, onExistingOccupierIdChange]);

  return (
    <FormStack substack spacing={0}>
      <FormField label={labelOverride || "Who is the existing occupant?"}>
        <RadioGroupInput
          onValueChange={onExistingOccupierIdChange}
          value={record.responsibleId}
          fields={fieldsFromPersons(persons, true)}
          error={errors?.responsibleId}
        />
      </FormField>

      {record.responsible && (
        <FormStack indented substack>
          <PersonDetailsFields
            excludeAddress
            serviceProvider={serviceProvider}
            record={record.responsible}
            update={updateExistingOccupier}
            errors={errors?.responsible}
          />
        </FormStack>
      )}
    </FormStack>
  );
};
